// React
import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";

// Formik
import { useFormik } from "formik";

// Validation
import {
  guestPatientValidationSchema,
  patientValidationSchema,
} from "Validation";

// Components
import { SwipeableDrawer, Tab, Fade, Stack, IconButton } from "@mui/material";
import { Close } from "@mui/icons-material";
import ConfirmEditModal from "Common/Modals/ConfirmEditModal/ConfirmEditModal.js";
import PatientDetails from "./PatientDetails";
import PatientHistory from "./PatientHistory";
import { MNTabs } from "Common/Components";

// Utils
import {
  getPatientFormInitialValues,
  updatePatientSearchResults,
  fetchAppointmentsByPatientId,
} from "./patientDrawerUtils";
import { formatPatientFormValues } from "Utils/PatientUtils";

// Styles
import { styles } from "./styles";

// Redux
import { useDispatch } from "react-redux";
import { openSnackbar } from "Store/SnackbarSlice";

const PatientDrawer = ({
  open,
  toggleDrawer,
  patient,
  setPatients,
  onPatientUpdated,
  matchesCurrentSearch,
  bookAppointment = undefined,
  isMBS = false,
}) => {
  const tabOptions = { details: 0, history: 1 };
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [confirmModal, setConfirmModal] = useState(false);
  const [formattedUserValues, setFormattedUserValues] = useState(
    patient || null
  );

  const [selectedTab, setSelectedTab] = useState(tabOptions.details);

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingMode, setBookingMode] = useState(false);

  const refreshPatientAppointments = async () => {
    await fetchAppointmentsByPatientId({
      patientId: patient?.id,
      setAppointments,
      setLoading,
      errorSnackbar,
    });
  };

  const handleCloseDrawer = () => {
    setSelectedTab(tabOptions.details);
    toggleDrawer(false);
    formik.resetForm();
  };

  const handleBookAppointment = async () => {
    if (!bookAppointment) {
      return undefined;
    }

    // Manually trigger validation for all fields and mark them as touched
    const errors = await formik.validateForm();

    formik.setTouched(
      Object.keys(formik.values).reduce((touchedFields, fieldName) => {
        touchedFields[fieldName] = true;
        return touchedFields;
      }, {})
    );

    if (Object.keys(errors).length > 0) {
      return undefined;
    }

    setFormattedUserValues(formatPatientFormValues(formik.values));
    setBookingMode(true);
    setConfirmModal(true);

    return undefined;
  };

  const updatePatientList = async (updatedPatient) => {
    try {
      const nextPatient = {
        ...patient,
        ...updatedPatient,
      };
      setPatients?.((patientList) =>
        updatePatientSearchResults({
          patientList,
          updatedPatient: nextPatient,
          matchesCurrentSearch,
        })
      );
      onPatientUpdated?.(nextPatient);
      formik.resetForm({
        values: getPatientFormInitialValues(nextPatient),
      });
    } catch (error) {
      dispatch(
        openSnackbar({
          message: error.message || t("errors.errorUpdatingPatientList"),
          severity: "error",
        })
      );
    }
  };

  const onContinueToBooking = bookingMode
    ? async (confirmedPatientValues) => {
        await bookAppointment({
          ...confirmedPatientValues,
          inSystem: patient?.inSystem ?? true,
        });
      }
    : undefined;

  const formik = useFormik({
    initialValues: getPatientFormInitialValues(patient),
    enableReinitialize: true,
    validationSchema:
      patient?.inSystem === false
        ? guestPatientValidationSchema(t)
        : patientValidationSchema(t),
    onSubmit: async (values) => {
      const { updatedAt, ...rest } = values;
      setFormattedUserValues(formatPatientFormValues(rest));
      setBookingMode(false);
      setConfirmModal(true);
    },
  });

  const errorSnackbar = useCallback(() => {
    dispatch(
      openSnackbar({
        message: t("errors.errorRefreshingAppointments"),
        severity: "error",
      })
    );
  }, [dispatch, t]);

  useEffect(() => {
    setFormattedUserValues(patient || null);
  }, [patient]);

  // Fetch appointments for the patient
  useEffect(() => {
    fetchAppointmentsByPatientId({
      patientId: patient?.id,
      setAppointments,
      setLoading,
      errorSnackbar,
    });
  }, [open, patient?.id, errorSnackbar]);

  return (
    <SwipeableDrawer
      anchor={"right"}
      open={open}
      sx={styles.drawer}
      onClose={handleCloseDrawer}
      onOpen={() => toggleDrawer(true)}
      disableSwipeToOpen
      disableDiscovery
    >
      <Stack width="100%">
        <Stack direction="row" justifyContent="center" alignItems="center">
          <MNTabs
            value={selectedTab}
            onChange={(_, newValue) => {
              setSelectedTab(newValue);
            }}
            tabName={"Patient Tabs"}
          >
            <Tab
              label={t("patientDrawer.tabs.details")}
              sx={styles.tabStyle(selectedTab === tabOptions.details)}
              value={tabOptions.details}
            />
            <Tab
              label={t("patientDrawer.tabs.history")}
              sx={styles.tabStyle(selectedTab === tabOptions.history)}
              value={tabOptions.history}
            />
          </MNTabs>
          <IconButton
            aria-label="Close patient drawer"
            onClick={handleCloseDrawer}
            sx={{ display: { xs: "inline-flex", sm: "none" } }}
          >
            <Close />
          </IconButton>
        </Stack>

        <Fade
          in={selectedTab === tabOptions.details}
          timeout={300}
          unmountOnExit
        >
          <div>
            <PatientDetails
              formik={formik}
              inSystem={
                typeof patient?.inSystem === "boolean" ? patient.inSystem : null
              }
              bookAppointment={handleBookAppointment}
              isMBS={isMBS}
            />
          </div>
        </Fade>
        <Fade
          in={selectedTab === tabOptions.history}
          timeout={300}
          unmountOnExit
        >
          <div>
            <PatientHistory
              appointments={appointments}
              setAppointments={setAppointments}
              refreshPatientAppointments={refreshPatientAppointments}
              loading={loading}
              patient={patient}
              isMBS={isMBS}
            />
          </div>
        </Fade>
      </Stack>

      <ConfirmEditModal
        open={confirmModal}
        onCloseModal={() => setConfirmModal(false)}
        patientValues={formattedUserValues || {}}
        mode={bookingMode ? "book" : "edit"}
        onContinueToBooking={onContinueToBooking}
        onUpdateSuccess={updatePatientList}
        updateSuccessDescription="confirmEditModal.patientUpdatedList"
        inSystem={isMBS ? Boolean(patient?.inSystem) : true}
        hasUpdatedInfo={formik.dirty}
      />
    </SwipeableDrawer>
  );
};

export default PatientDrawer;
